import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLlmProfile } from '@/lib/api/generated/api';

import { toLlmProfileError } from '../errors';
import { llmProfileKeys } from '../queryKeys';
import type { LlmProfile, LlmProfileWriteInput } from '../types';

/**
 * Edit an existing LLM profile.
 *
 * The form always resends the full next state, so this never needs to omit
 * a field to mean "leave unchanged" (even though the generated
 * `UpdateLLMProfileRequest` supports that via omission).
 */
export function useUpdateLlmProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slug,
      body,
    }: {
      slug: string;
      body: LlmProfileWriteInput;
    }): Promise<LlmProfile> => {
      const response = await updateLlmProfile(slug, body);
      if (response.status === 200) return response.data;
      throw toLlmProfileError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: llmProfileKeys.all() });
    },
  });
}
